import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PolyHero } from "./PolyHero";
import { SignalCard } from "./SignalCard";
import { AccountCard } from "./AccountCard";
import { TradesTable } from "./TradesTable";
import { RulesCard } from "./RulesCard";
import { LiveTracker } from "./LiveTracker";
import { useSignalAlerts, AlertsButton } from "./Alerts";
import { SettingsCard } from "./SettingsCard";
import { Scoreboard } from "./Scoreboard";
import { BudgetDialog } from "./BudgetDialog";
import { TelegramCard } from "./TelegramCard";
import { CalibrationCard } from "./CalibrationCard";
import { NotificationLog } from "./NotificationLog";
import { DiagnosticsCard } from "./DiagnosticsCard";
import { SignalFilterCard } from "./SignalFilterCard";
import { PMAccountCard } from "./PMAccountCard";
import { SignalTrackCard } from "./SignalTrackCard";
import { ProfitChart } from "./ProfitChart";
import { SimpleView, HowToCard } from "./SimpleView";
import { ProCard, OwnerButton } from "./ProAndTrading";
import { LiveTradingCard } from "./LiveTradingCard";
import { useAccess } from "@/lib/access";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BotPage() {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [lp, setLp] = useState(null);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [acct, setAcct] = useState(null);
  const access = useAccess();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bot/state`);
      setState(data);
    } catch {
      toast.error("Couldn't reach the bot. Retrying…");
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 10000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    const loadPrice = () => axios.get(`${API}/bot/price`).then((r) => setLp(r.data)).catch(() => {});
    loadPrice();
    const fast = setInterval(loadPrice, 2000);
    return () => { clearInterval(poll); clearInterval(clock); clearInterval(fast); };
  }, [load]);

  const toggle = async (enabled) => {
    await axios.post(`${API}/bot/toggle`, { enabled });
    toast.success(enabled ? "Practice bot turned ON" : "Practice bot paused");
    load();
  };
  const reset = async () => {
    await axios.post(`${API}/bot/reset`);
    toast.success(`Practice account reset to $${state?.account?.start_balance ?? 100}`);
    load();
  };

  const live = state?.live;
  const alerts = useSignalAlerts(live);
  const minute = Math.floor(now / 60000);
  useEffect(() => {
    if (access.owner && state?.account && !state.account.onboarded) setBudgetOpen(true);
  }, [state?.account?.onboarded, access.owner]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (access.owner) axios.get(`${API}/account`).then((r) => setAcct(r.data)).catch(() => setAcct(null));
    else setAcct(null);
  }, [access.owner, minute]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6" data-testid="bot-page">
      <div className="lg:col-span-12 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-400 max-w-xl">Polymarket US · Bitcoin 15-minute Up/Down. The big box tells you <b>BUY UP</b>, <b>BUY DOWN</b>, <b>WAIT</b> or <b>SELL</b>.</p>
        <div className="flex items-center gap-3"><AlertsButton {...alerts} /><OwnerButton access={access} /></div>
      </div>
      <div className="lg:col-span-7 min-w-0"><SimpleView live={live} account={acct} rules={state?.rules} now={now} /></div>
      <div className="lg:col-span-5 min-w-0 space-y-4">
        <HowToCard rules={state?.rules} />
        {!access.pro && <ProCard access={access} />}
      </div>
      {access.owner && <div className="lg:col-span-12 min-w-0"><LiveTradingCard /></div>}
      {access.owner && <div className="lg:col-span-12 min-w-0"><PMAccountCard tick={minute} /></div>}
      {access.owner && <div className="lg:col-span-12 min-w-0"><TelegramCard /></div>}
      {access.pro && (
        <div className="lg:col-span-12">
          <button onClick={() => setShowAll((v) => !v)} className="w-full py-3 rounded-full border border-[#1A2338] text-sm text-slate-300 hover:bg-[#0B1220]" data-testid="show-details-btn">
            {showAll ? "Hide details" : "Show all details (charts, track record, practice bot, diagnostics)"}
          </button>
        </div>
      )}
      {access.pro && showAll && <>
      {state?.error && live && (
        <div className="lg:col-span-12 card p-4 text-sm text-amber-300 border-amber-500/40" data-testid="bot-data-warning">
          Data unavailable: {state.error}. Showing the last valid reading{live.updated ? ` from ${new Date(live.updated).toLocaleTimeString()}` : ""}; no new estimate is produced.
        </div>
      )}
      {state?.error && !live && (
        <div className="lg:col-span-12 card p-4 text-sm text-red-400" data-testid="bot-error">Bot problem: {state.error}</div>
      )}
      <BudgetDialog open={budgetOpen} onOpenChange={setBudgetOpen} account={state?.account} onSaved={load} />
      <div className="lg:col-span-5 min-w-0"><PolyHero live={live} lp={lp} now={now} rules={state?.rules} /></div>
      <div className="lg:col-span-7 space-y-5 min-w-0"><SignalFilterCard live={live} /><LiveTracker live={live} lp={lp} /></div>
      <div className="lg:col-span-12 min-w-0"><SignalCard live={live} account={state?.account} /></div>
      <div className="lg:col-span-12 min-w-0"><AccountCard account={state?.account} live={live} onToggle={toggle} onReset={reset} onBudget={() => setBudgetOpen(true)} /></div>
      <div className="lg:col-span-12 min-w-0"><ProfitChart account={state?.account} /></div>
      <div className="lg:col-span-12 min-w-0"><SettingsCard rules={state?.rules} onSaved={load} /></div>
      <div className="lg:col-span-12 min-w-0"><SignalTrackCard tick={minute} /></div>
      <div className="lg:col-span-12 min-w-0"><CalibrationCard /></div>
      <div className="lg:col-span-12 min-w-0"><Scoreboard tick={live?.market?.slug + minute} /></div>
      <div className="lg:col-span-12 min-w-0"><NotificationLog tick={minute} /></div>
      <div className="lg:col-span-12 min-w-0"><DiagnosticsCard /></div>
      <div className="lg:col-span-12 min-w-0"><RulesCard rules={state?.rules} /></div>
      <div className="lg:col-span-12 min-w-0"><TradesTable trades={state?.account?.trades || []} /></div>
      </>}
    </div>
  );
}
